import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  SearchField,
  Skeleton,
  Text,
  EmptyPlaceholder,
  useSearching,
  isDefinedString,
} from '@client/toolkit';
import { PackageType, SpaceHierarchyReadModel, useGetBuildingsQuery } from '@client/api';
import { SpaceHierarchyTree } from './space-hierarchy-tree';

export interface SpaceHierarchySelectorProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedSpaces: SpaceHierarchyReadModel[]) => void;
  selected?: SpaceHierarchyReadModel[];
  title?: string;
  packageFilter?: PackageType;
  allowMultiSelect?: boolean;
  showBuildingSelector?: boolean;
}

export function SpaceHierarchySelector({
  open,
  onClose,
  onSubmit,
  selected = [],
  title,
  packageFilter,
  allowMultiSelect = true,
  showBuildingSelector = true,
}: SpaceHierarchySelectorProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [selectedSpaces, setSelectedSpaces] = useState<SpaceHierarchyReadModel[]>(selected);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null);

  const { search, debouncedSearch, updateSearch, clearSearch, hasSearch } = useSearching();

  const { data: buildingsData, isFetching: isFetchingBuildings } = useGetBuildingsQuery(
    {
      search: debouncedSearch ?? '',
      'pagination.Limit': 100,
      'pagination.Offset': 0,
      'requiredPackage.Equal': packageFilter,
    },
    { skip: !showBuildingSelector },
  );

  const buildings = useMemo(() => buildingsData?.items ?? [], [buildingsData]);

  useEffect(() => {
    if (showBuildingSelector && buildings.length === 1 && !selectedBuildingId) {
      setSelectedBuildingId(buildings[0].id);
    }
  }, [buildings, selectedBuildingId, showBuildingSelector]);

  const handleToggleSpace = useCallback(
    (space: SpaceHierarchyReadModel, isSelected: boolean) => {
      if (!allowMultiSelect) {
        setSelectedSpaces(isSelected ? [space] : []);
        return;
      }

      if (isSelected) {
        setSelectedSpaces((prev) => [...prev, space]);
      } else {
        setSelectedSpaces((prev) => prev.filter((s) => s.id !== space.id));
      }
    },
    [allowMultiSelect],
  );

  const handleRemoveSpace = useCallback((spaceId: string) => {
    setSelectedSpaces((prev) => prev.filter((s) => s.id !== spaceId));
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit(selectedSpaces);
    onClose();
  }, [selectedSpaces, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    setSelectedSpaces(selected);
    onClose();
  }, [selected, onClose]);

  useEffect(() => {
    if (open) {
      setSelectedSpaces(selected);
    }
  }, [open, selected]);

  const modalTitle =
    title ??
    tCommon('selectEntity', {
      entity: tGlobal('entity.space', { context: allowMultiSelect ? 'plural' : 'singular' }).toLowerCase(),
    });

  return (
    <Modal open={open} appear size="3xl" onClose={handleClose} onBackdropClick={handleClose} title={modalTitle}>
      <ModalContent>
        <div className="flex flex-col gap-4 px-5 h-[600px]">
          <div className="flex flex-col gap-2">
            <SearchField
              value={search}
              onChange={updateSearch}
              onClear={clearSearch}
              placeholder={tCommon('search')}
              progressing={isFetchingBuildings && hasSearch}
            />

            {showBuildingSelector && (
              <div className="flex flex-col gap-2">
                <Text className="text-sm font-medium text-white/70">{tGlobal('entity.building')}</Text>
                {isFetchingBuildings ? (
                  <Skeleton className="h-10 w-full" />
                ) : buildings.length === 0 ? (
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.building', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {buildings.map((building) => (
                      <Button
                        key={building.id}
                        size="sm"
                        color={selectedBuildingId === building.id ? 'primary' : 'primary-invert'}
                        onClick={() => setSelectedBuildingId(building.id)}
                      >
                        {building.name}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-hidden flex gap-4">
            <div className="flex-1 overflow-y-auto border border-white/10 rounded-lg p-4">
              {selectedBuildingId ? (
                <SpaceHierarchyTree
                  buildingId={selectedBuildingId}
                  selectedSpaces={selectedSpaces}
                  onToggleSpace={handleToggleSpace}
                  searchQuery={debouncedSearch ?? undefined}
                />
              ) : (
                <EmptyPlaceholder>
                  {tCommon('selectEntity', { entity: tGlobal('entity.building').toLowerCase() })}
                </EmptyPlaceholder>
              )}
            </div>

            <div className="flex-1 shrink-0 overflow-y-auto border border-white/10 rounded-lg p-4">
              <Text className="text-sm font-medium text-white/70 mb-3">
                {tCommon('selected')} ({selectedSpaces.length})
              </Text>
              <div className="flex flex-col gap-2 py-4">
                {selectedSpaces.length === 0 ? (
                  <EmptyPlaceholder>{tCommon('noEntitySelected.entity.space')}</EmptyPlaceholder>
                ) : (
                  selectedSpaces.map((space) => (
                    <div
                      key={space.id}
                      className="flex items-center justify-between gap-2 bg-surface-selected rounded-lg px-3 py-2"
                    >
                      <Text
                        className="text-sm flex-1 truncate"
                        isEmpty={!isDefinedString((space as any).displayPath ?? space.name)}
                      >
                        {(space as any).displayPath ?? space.name}
                      </Text>
                      <Button size="xs" color="primary-invert" onClick={() => handleRemoveSpace(space.id)}>
                        ×
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalContent>

      <ModalFooter>
        <Button disabled={selectedSpaces.length === 0} onClick={handleSubmit}>
          {tCommon('save')}
        </Button>
        <Button color="primary-dark" onClick={handleClose}>
          {tCommon('close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
